import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ConversationDraft } from 'apis/userStorage';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import {
  CONVERSATION_DRAFTS_TAB_ID,
  conversationDraftsChanged,
} from './conversationDrafts.actions';
import {
  ConversationDraftField,
  ConversationDraftPatch,
  ConversationDraftContent,
} from './conversationDrafts.types';
import {
  applyConversationDraftWrite,
  CONVERSATION_DRAFT_DEBOUNCE_MS,
  CONVERSATION_DRAFT_MAX_WAIT_MS,
  isConversationDraftUnchanged,
} from './conversationDrafts.utils';

/** Reads and writes the stored draft of a single conversation. */
export type UseConversationDraftResult = {
  /** The stored draft, or null when the conversation has none. */
  readonly draft: ConversationDraft | null;
  /** Merges draft fields into the stored draft, debounced. */
  readonly saveDraft: (merge: Partial<ConversationDraftContent>) => void;
  /** Drops the given fields, or the whole draft when called with none. */
  readonly clearDraft: (fields?: readonly ConversationDraftField[]) => void;
  /** Writes any pending change immediately. */
  readonly flushDraft: () => void;
};

/** The draft of one conversation, stored per user on the server. */
export const useConversationDraft = (
  csid: number | null | undefined,
): UseConversationDraftResult => {
  const { data: userStorage, set: setUserStorage } = useServerStorage('user');
  const dispatch = useAppDispatch();

  const draftKey = csid == null ? null : String(csid);
  const draft =
    (draftKey && userStorage.conversation_drafts?.[draftKey]) || null;

  const setUserStorageRef = useRef(setUserStorage);
  const storedDraftsRef = useRef(userStorage.conversation_drafts);
  /** The change accumulated since the last write, and the csid it belongs to. */
  const pendingRef = useRef<{
    draftKey: string;
    patch: ConversationDraftPatch;
  } | null>(null);

  useEffect(() => {
    setUserStorageRef.current = setUserStorage;
    storedDraftsRef.current = userStorage.conversation_drafts;
  });

  const writeDraft = useCallback(
    (patch: ConversationDraftPatch, key: string | null) => {
      if (!key) return;

      const storedDraft = storedDraftsRef.current?.[key] ?? null;
      if (isConversationDraftUnchanged(storedDraft, patch)) return;

      setUserStorageRef.current({
        conversation_drafts: (drafts) =>
          applyConversationDraftWrite(drafts, {
            csid: key,
            patch,
            now: Date.now(),
          }),
      });

      dispatch(
        conversationDraftsChanged({ tabId: CONVERSATION_DRAFTS_TAB_ID }),
      );
    },
    [dispatch],
  );

  const writePendingDraft = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;

    pendingRef.current = null;
    writeDraft(pending.patch, pending.draftKey);
  }, [writeDraft]);

  const writePendingDraftRef = useRef(writePendingDraft);
  useEffect(() => {
    writePendingDraftRef.current = writePendingDraft;
  });

  // Created once: rebuilding it would restart the timer.
  const scheduleWrite = useMemo(
    () =>
      debounce(
        () => writePendingDraftRef.current(),
        CONVERSATION_DRAFT_DEBOUNCE_MS,
        { maxWait: CONVERSATION_DRAFT_MAX_WAIT_MS },
      ),
    [],
  );

  const saveDraft = useCallback<UseConversationDraftResult['saveDraft']>(
    (merge) => {
      if (!draftKey) return;

      const pendingPatch =
        pendingRef.current?.draftKey === draftKey
          ? pendingRef.current.patch
          : undefined;

      pendingRef.current = {
        draftKey,
        patch: { ...pendingPatch, merge: { ...pendingPatch?.merge, ...merge } },
      };
      scheduleWrite();
    },
    [draftKey, scheduleWrite],
  );

  const clearDraft = useCallback<UseConversationDraftResult['clearDraft']>(
    (fields) => {
      scheduleWrite.cancel();
      pendingRef.current = null;

      writeDraft(fields ? { remove: fields } : { clear: true }, draftKey);
    },
    [draftKey, scheduleWrite, writeDraft],
  );

  const flushDraft = useCallback(() => {
    scheduleWrite.cancel();
    writePendingDraftRef.current();
  }, [scheduleWrite]);

  useEffect(() => () => flushDraft(), [flushDraft]);

  return useMemo(
    () => ({ draft, saveDraft, clearDraft, flushDraft }),
    [draft, saveDraft, clearDraft, flushDraft],
  );
};
